import * as Headless from "@headlessui/react";
import clsx from "clsx";
import type React from "react";

export function CheckboxGroup({
	className,
	...props
}: React.ComponentPropsWithoutRef<"div">) {
	return (
		<div
			data-slot="control"
			{...props}
			className={clsx(
				className,
				// Basic groups
				"space-y-3",
				// With descriptions
				"has-data-[slot=description]:space-y-6 has-data-[slot=description]:**:data-[slot=label]:font-medium",
			)}
		/>
	);
}

export function CheckboxField({
	className,
	...props
}: { className?: string } & Omit<Headless.FieldProps, "as" | "className">) {
	return (
		<Headless.Field
			data-slot="field"
			{...props}
			className={clsx(
				className,
				// Base layout
				"flex items-center gap-4",
				// With description
				"has-data-[slot=description]:**:data-[slot=label]:font-medium",
			)}
		/>
	);
}

const base = [
	// Basic layout
	"relative isolate flex size-4.5 items-center justify-center rounded-[0.3125rem] sm:size-4",
	// Background color + shadow applied to inset pseudo element, so shadow blends with border in light mode
	"before:absolute before:inset-0 before:-z-10 before:rounded-[calc(0.3125rem-1px)] before:bg-base-100 before:shadow-sm",
	// Background color when checked
	"group-data-checked:before:bg-(--checkbox-checked-bg)",
	// Background color is moved to control and shadow is removed in dark mode so hide `before` pseudo
	"dark:before:hidden",
	// Background color applied to control in dark mode
	"dark:bg-base-content/5 dark:group-data-checked:bg-(--checkbox-checked-bg)",
	// Border
	"border border-base-content/15 group-data-checked:border-transparent group-data-hover:group-data-checked:border-transparent group-data-hover:border-base-content/30 group-data-checked:bg-(--checkbox-checked-border)",
	"dark:border-base-content/15 dark:group-data-checked:border-base-content/5 dark:group-data-hover:group-data-checked:border-base-content/5 dark:group-data-hover:border-base-content/30",
	// Inner highlight shadow
	"after:absolute after:inset-0 after:rounded-[calc(0.3125rem-1px)] after:shadow-[inset_0_1px_--theme(--color-white/15%)]",
	"dark:after:-inset-px dark:after:hidden dark:after:rounded-[0.3125rem] dark:group-data-checked:after:block",
	// Focus ring
	"group-data-focus:outline-2 group-data-focus:outline-offset-2 group-data-focus:outline-primary",
	// Disabled state
	"group-data-disabled:opacity-50",
	"group-data-disabled:border-base-content/25 group-data-disabled:bg-base-content/5 group-data-disabled:[--checkbox-check:var(--color-base-content)]/50 group-data-disabled:before:bg-transparent",
	"dark:group-data-disabled:border-base-content/20 dark:group-data-disabled:bg-base-content/2.5 dark:group-data-disabled:[--checkbox-check:var(--color-base-100)]/50 dark:group-data-checked:group-data-disabled:after:hidden",
	// Forced colors mode
	"forced-colors:[--checkbox-check:HighlightText] forced-colors:[--checkbox-checked-bg:Highlight] forced-colors:group-data-disabled:[--checkbox-check:Highlight]",
	"dark:forced-colors:[--checkbox-check:HighlightText] dark:forced-colors:[--checkbox-checked-bg:Highlight] dark:forced-colors:group-data-disabled:[--checkbox-check:Highlight]",
];

const colors = {
	"dark/zinc": [
		"[--checkbox-check:var(--color-primary-content)] [--checkbox-checked-bg:var(--color-primary)] [--checkbox-checked-border:var(--color-primary)]/90",
		"dark:[--checkbox-checked-bg:var(--color-primary)]",
	],
	"dark/white": [
		"[--checkbox-check:var(--color-primary-content)] [--checkbox-checked-bg:var(--color-primary)] [--checkbox-checked-border:var(--color-primary)]/90",
		"dark:[--checkbox-check:var(--color-primary-content)] dark:[--checkbox-checked-bg:var(--color-primary)] dark:[--checkbox-checked-border:var(--color-primary)]/15",
	],
	white:
		"[--checkbox-check:var(--color-zinc-900)] [--checkbox-checked-bg:var(--color-white)] [--checkbox-checked-border:var(--color-zinc-950)]/15",
	dark: "[--checkbox-check:var(--color-white)] [--checkbox-checked-bg:var(--color-zinc-900)] [--checkbox-checked-border:var(--color-zinc-950)]/90",
	zinc: "[--checkbox-check:var(--color-white)] [--checkbox-checked-bg:var(--color-zinc-600)] [--checkbox-checked-border:var(--color-zinc-700)]/90",
	red: "[--checkbox-check:var(--color-error-content)] [--checkbox-checked-bg:var(--color-error)] [--checkbox-checked-border:var(--color-error)]/90",
	orange:
		"[--checkbox-check:var(--color-warning-content)] [--checkbox-checked-bg:var(--color-warning)] [--checkbox-checked-border:var(--color-warning)]/90",
	amber:
		"[--checkbox-check:var(--color-warning-content)] [--checkbox-checked-bg:var(--color-warning)] [--checkbox-checked-border:var(--color-warning)]/80",
	yellow:
		"[--checkbox-check:var(--color-warning-content)] [--checkbox-checked-bg:var(--color-warning)] [--checkbox-checked-border:var(--color-warning)]/80",
	lime: "[--checkbox-check:var(--color-success-content)] [--checkbox-checked-bg:var(--color-success)] [--checkbox-checked-border:var(--color-success)]/80",
	green:
		"[--checkbox-check:var(--color-success-content)] [--checkbox-checked-bg:var(--color-success)] [--checkbox-checked-border:var(--color-success)]/90",
	emerald:
		"[--checkbox-check:var(--color-success-content)] [--checkbox-checked-bg:var(--color-success)] [--checkbox-checked-border:var(--color-success)]/90",
	teal: "[--checkbox-check:var(--color-success-content)] [--checkbox-checked-bg:var(--color-success)] [--checkbox-checked-border:var(--color-success)]/90",
	cyan: "[--checkbox-check:var(--color-cyan-950)] [--checkbox-checked-bg:var(--color-cyan-300)] [--checkbox-checked-border:var(--color-cyan-400)]/80",
	sky: "[--checkbox-check:var(--color-info-content)] [--checkbox-checked-bg:var(--color-info)] [--checkbox-checked-border:var(--color-info)]/80",
	blue: "[--checkbox-check:var(--color-info-content)] [--checkbox-checked-bg:var(--color-info)] [--checkbox-checked-border:var(--color-info)]/90",
	indigo:
		"[--checkbox-check:var(--color-primary-content)] [--checkbox-checked-bg:var(--color-primary)] [--checkbox-checked-border:var(--color-primary)]/90",
	violet:
		"[--checkbox-check:var(--color-primary-content)] [--checkbox-checked-bg:var(--color-primary)] [--checkbox-checked-border:var(--color-primary)]/90",
	purple:
		"[--checkbox-check:var(--color-primary-content)] [--checkbox-checked-bg:var(--color-primary)] [--checkbox-checked-border:var(--color-primary)]/90",
	fuchsia:
		"[--checkbox-check:var(--color-secondary-content)] [--checkbox-checked-bg:var(--color-secondary)] [--checkbox-checked-border:var(--color-secondary)]/90",
	pink: "[--checkbox-check:var(--color-secondary-content)] [--checkbox-checked-bg:var(--color-secondary)] [--checkbox-checked-border:var(--color-secondary)]/90",
	rose: "[--checkbox-check:var(--color-secondary-content)] [--checkbox-checked-bg:var(--color-secondary)] [--checkbox-checked-border:var(--color-secondary)]/90",
};

type Color = keyof typeof colors;

import { forwardRef } from "react";

export const Checkbox = forwardRef(function Checkbox(
	{
		color = "dark/zinc",
		className,
		...props
	}: {
		color?: Color;
		className?: string;
	} & Omit<Headless.CheckboxProps, "as" | "className">,
	ref: React.ForwardedRef<HTMLButtonElement>,
) {
	return (
		<Headless.Checkbox
			ref={ref}
			data-slot="control"
			{...props}
			className={clsx(className, "group inline-flex focus:outline-hidden")}
		>
			<span className={clsx([base, colors[color]])}>
				<svg
					className="size-4 stroke-(--checkbox-check) opacity-0 group-data-checked:opacity-100 sm:h-3.5 sm:w-3.5"
					viewBox="0 0 14 14"
					fill="none"
					aria-hidden="true"
				>
					{/* Checkmark icon */}
					<path
						className="opacity-100 group-data-indeterminate:opacity-0"
						d="M3 8L6 11L11 3.5"
						strokeWidth={2}
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
					{/* Indeterminate icon */}
					<path
						className="opacity-0 group-data-indeterminate:opacity-100"
						d="M3 7H11"
						strokeWidth={2}
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</svg>
			</span>
		</Headless.Checkbox>
	);
});
